import { type FragmentOf, readFragment } from "@opensea/graphql"
import {
  FlexCenter,
  NumberDisplay,
  Separator,
  SpaceBetween,
  TextBody,
  Tooltip,
} from "@opensea/ui-kit"
import { Info } from "@opensea/ui-kit/icons"
import { useTranslations } from "use-intl"
import { CreatorEarningsToggle } from "../CreateOrdersSummary/CreatorEarningsToggle"
import { CreatorEarningsTooltip } from "../CreateOrdersSummary/CreatorEarningsTooltip"
import {
  getFloorPriceDifference,
  OrdersSummary,
  OrdersSummaryItem,
  OrdersSummaryItemContent,
  OrdersSummaryItemDescription,
  OrdersSummaryItemSide,
  OrdersSummaryItemTitle,
  OrdersSummaryTokenValue,
  OrdersSummaryTokenValueSkeleton,
} from "../OrdersSummary"
import { AcceptOffersSummaryFragment } from "./AcceptOffersSummary.fragment"
import {
  analyzeCreatorFeeStates,
  type CreatorFeeBreakdown,
  calculateTotalQuantity,
  categorizeCollectionFees,
  groupItemQuotesByCollection,
} from "./utils"

type AcceptOffersSummaryProps = {
  quote: FragmentOf<typeof AcceptOffersSummaryFragment>
  applyCreatorFee?: boolean
  onCreatorFeeChange?: (enabled: boolean) => void
}

// Helper component for optional creator fees toggle
function CreatorEarningsToggleSection({
  hasOptionalCreatorFees,
  applyCreatorFee,
  onToggle,
  t,
}: {
  hasOptionalCreatorFees: boolean
  applyCreatorFee: boolean
  onToggle: (enabled: boolean) => void
  t: ReturnType<typeof useTranslations<"wallet.AcceptOffersSummary">>
}) {
  if (!hasOptionalCreatorFees) {
    return null
  }

  return (
    <OrdersSummaryItem>
      <OrdersSummaryItemContent>
        <SpaceBetween className="w-full">
          <TextBody weight="semibold">{t("creatorEarnings")}</TextBody>
          <CreatorEarningsToggle
            checked={applyCreatorFee}
            creatorFeeEnforced={false}
            onCheckedChange={onToggle}
          />
        </SpaceBetween>
      </OrdersSummaryItemContent>
    </OrdersSummaryItem>
  )
}

// Helper component for optional creator fees breakdown
function OptionalCreatorFeesSection({
  optionalCreatorFees,
  applyCreatorFee,
  total,
}: {
  optionalCreatorFees: CreatorFeeBreakdown[]
  applyCreatorFee: boolean
  total: { unit: number; symbol: string }
}) {
  if (!applyCreatorFee) {
    return null
  }

  return (
    <>
      {optionalCreatorFees.map((collectionFee, idx) => (
        <OrdersSummaryItem key={`optional-${idx}`}>
          <OrdersSummaryItemContent>
            <OrdersSummaryItemDescription className="shrink-0">
              {collectionFee.collectionName} (
              <NumberDisplay
                display="percent"
                value={collectionFee.feeBasisPoints / 10_000}
              />
              )
            </OrdersSummaryItemDescription>
          </OrdersSummaryItemContent>
          <OrdersSummaryItemSide>
            <OrdersSummaryTokenValue
              isFee
              symbol={total.symbol}
              usdValue={collectionFee.feeUsd}
              value={collectionFee.fee}
            />
          </OrdersSummaryItemSide>
        </OrdersSummaryItem>
      ))}
    </>
  )
}

// Helper component for required creator fees breakdown
function RequiredCreatorFeesSection({
  requiredCreatorFees,
  total,
}: {
  requiredCreatorFees: CreatorFeeBreakdown[]
  total: { unit: number; symbol: string }
}) {
  return (
    <>
      {requiredCreatorFees.map((collectionFee, idx) => (
        <OrdersSummaryItem key={`required-${idx}`}>
          <OrdersSummaryItemContent>
            <FlexCenter className="gap-1.5">
              <OrdersSummaryItemDescription className="shrink-0">
                {collectionFee.collectionName} (
                <NumberDisplay
                  display="percent"
                  value={collectionFee.feeBasisPoints / 10_000}
                />
                )
              </OrdersSummaryItemDescription>

              <CreatorEarningsTooltip
                creatorFeeEnforced={true}
                forAcceptOffersSummary={true}
              />
            </FlexCenter>
          </OrdersSummaryItemContent>
          <OrdersSummaryItemSide>
            <OrdersSummaryTokenValue
              isFee
              symbol={total.symbol}
              usdValue={collectionFee.feeUsd}
              value={collectionFee.fee}
            />
          </OrdersSummaryItemSide>
        </OrdersSummaryItem>
      ))}
    </>
  )
}

export function AcceptOffersSummary({
  quote: quoteKey,
  applyCreatorFee,
  onCreatorFeeChange,
}: AcceptOffersSummaryProps) {
  const t = useTranslations("wallet.AcceptOffersSummary")
  const quote = readFragment(AcceptOffersSummaryFragment, quoteKey)
  const pricePerToken = quote.totalPrice.pricePerToken
  const fees = quote.feeSummary

  const collections = groupItemQuotesByCollection(quote.itemQuotes)
  const { hasOptionalCreatorFees } = analyzeCreatorFeeStates(collections)

  const handleCreatorFeeToggle = (enabled: boolean) => {
    onCreatorFeeChange?.(enabled)
  }

  const firstItemQuote = quote.itemQuotes[0]
  const floorPrice =
    quote.itemQuotes.length === 1 && firstItemQuote?.__typename === "ItemQuote"
      ? firstItemQuote.item.collection?.floorPrice?.pricePerItem.usd
      : undefined
  const floorPriceDifference = getFloorPriceDifference(
    floorPrice,
    pricePerToken[0]?.usd,
    firstItemQuote?.__typename === "ItemQuote" ? firstItemQuote.quantity : 1,
  )

  return pricePerToken.map(({ token: total, usd }, index) => {
    const shouldApplyCreatorFee = applyCreatorFee ?? false

    const totalQuantity = calculateTotalQuantity(quote.itemQuotes)

    const collectionFees = categorizeCollectionFees(
      collections,
      total,
      usd,
      totalQuantity,
      shouldApplyCreatorFee,
    )

    const marketplaceFee =
      total.unit * (fees.marketplaceFeeBasisPoints / 10_000)
    const marketplaceFeeUsd = usd * (fees.marketplaceFeeBasisPoints / 10_000)

    const totalCreatorFee =
      collectionFees.totalOptionalFee + collectionFees.totalRequiredFee
    const totalCreatorFeeUsd =
      collectionFees.totalOptionalFeeUsd + collectionFees.totalRequiredFeeUsd

    const totalWithoutFees = total.unit - totalCreatorFee - marketplaceFee
    const openseaFeePercentage = fees.marketplaceFeeBasisPoints / 10_000

    return (
      <OrdersSummary key={index}>
        {floorPriceDifference && (
          <OrdersSummaryItem>
            <OrdersSummaryItemContent>
              <OrdersSummaryItemDescription>
                {t("floorDifference")}
              </OrdersSummaryItemDescription>
            </OrdersSummaryItemContent>
            <OrdersSummaryItemSide>
              <OrdersSummaryItemDescription className="font-mono">
                <NumberDisplay display="percent" value={floorPriceDifference} />{" "}
                {floorPriceDifference.gt(0) ? t("aboveFloor") : t("belowFloor")}
              </OrdersSummaryItemDescription>
            </OrdersSummaryItemSide>
          </OrdersSummaryItem>
        )}

        <OrdersSummaryItem>
          <OrdersSummaryItemContent>
            <FlexCenter className="gap-1.5">
              <OrdersSummaryItemDescription>
                {t("platformFees")}{" "}
                {!Number.isNaN(openseaFeePercentage) && (
                  <>
                    (
                    <NumberDisplay
                      display="percent"
                      value={openseaFeePercentage}
                    />
                    )
                  </>
                )}
              </OrdersSummaryItemDescription>
              <Tooltip
                content={<TextBody>{t("platformFeesTooltip")}</TextBody>}
                side="bottom"
              >
                <Info fill="text-secondary" size={16} />
              </Tooltip>
            </FlexCenter>
          </OrdersSummaryItemContent>
          <OrdersSummaryItemSide>
            <OrdersSummaryTokenValue
              isFee
              symbol={total.symbol}
              usdValue={marketplaceFeeUsd}
              value={marketplaceFee}
            />
          </OrdersSummaryItemSide>
        </OrdersSummaryItem>

        <CreatorEarningsToggleSection
          applyCreatorFee={applyCreatorFee ?? false}
          hasOptionalCreatorFees={hasOptionalCreatorFees}
          onToggle={handleCreatorFeeToggle}
          t={t}
        />

        <OptionalCreatorFeesSection
          applyCreatorFee={applyCreatorFee ?? false}
          optionalCreatorFees={collectionFees.optionalCreatorFees}
          total={total}
        />

        <RequiredCreatorFeesSection
          requiredCreatorFees={collectionFees.requiredCreatorFees}
          total={total}
        />

        <Separator />

        <OrdersSummaryItem>
          <OrdersSummaryItemContent>
            <OrdersSummaryItemTitle>
              {t("totalEstProceeds")}
            </OrdersSummaryItemTitle>
          </OrdersSummaryItemContent>
          <OrdersSummaryItemSide>
            <OrdersSummaryTokenValue
              symbol={total.symbol}
              usdValue={usd - totalCreatorFeeUsd - marketplaceFeeUsd}
              value={totalWithoutFees}
              variant="result"
            />
          </OrdersSummaryItemSide>
        </OrdersSummaryItem>
      </OrdersSummary>
    )
  })
}

export function AcceptOffersSummarySkeleton() {
  const t = useTranslations("wallet.AcceptOffersSummary")

  return (
    <OrdersSummary>
      <OrdersSummaryItem>
        <OrdersSummaryItemContent>
          <OrdersSummaryItemDescription>
            {t("floorDifference")}
          </OrdersSummaryItemDescription>
        </OrdersSummaryItemContent>
        <OrdersSummaryItemSide>
          <OrdersSummaryTokenValueSkeleton />
        </OrdersSummaryItemSide>
      </OrdersSummaryItem>

      <OrdersSummaryItem>
        <OrdersSummaryItemContent>
          <OrdersSummaryItemDescription>
            {t("platformFees")}
          </OrdersSummaryItemDescription>
        </OrdersSummaryItemContent>
        <OrdersSummaryItemSide>
          <OrdersSummaryTokenValueSkeleton />
        </OrdersSummaryItemSide>
      </OrdersSummaryItem>

      <Separator />

      <OrdersSummaryItem>
        <OrdersSummaryItemContent>
          <OrdersSummaryItemTitle>
            {t("totalEstProceeds")}
          </OrdersSummaryItemTitle>
        </OrdersSummaryItemContent>
        <OrdersSummaryItemSide>
          <OrdersSummaryTokenValueSkeleton variant="result" />
        </OrdersSummaryItemSide>
      </OrdersSummaryItem>
    </OrdersSummary>
  )
}
